import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type MenuState = {
  open: boolean;
  x: number;
  y: number;
  items: React.ReactNode | null;
  context?: any;
  options?: Record<string, any> | null;
  triggerId?: string | null;
};

type RightClickContextType = {
  menu: MenuState;
  openMenu: (m: Omit<MenuState, "open"> & { triggerId?: string }) => void;
  updateMenu: (m: Partial<MenuState>) => void;
  closeMenu: () => void;
};

const RightClickContext = createContext<RightClickContextType | undefined>(undefined);

export function useRightClick() {
  const ctx = useContext(RightClickContext);
  if (!ctx) throw new Error("useRightClick must be used within RightClickProvider");
  return ctx;
}

export default function RightClickProvider({ children }: { children: React.ReactNode }) {
  const [menu, setMenu] = useState<MenuState>({
    open: false,
    x: 0,
    y: 0,
    items: null,
    context: null,
    options: null,
    triggerId: null,
  });

  const menuRef = useRef<HTMLDivElement | null>(null);

  const openMenu = useCallback((m: Omit<MenuState, "open"> & { triggerId?: string }) => {
    setMenu({
      open: true,
      x: m.x,
      y: m.y,
      items: m.items ?? null,
      context: m.context,
      options: m.options ?? null,
      triggerId: m.triggerId ?? null,
    });
  }, []);

  const updateMenu = useCallback((m: Partial<MenuState>) => {
    setMenu((prev) => ({ ...prev, ...m }));
  }, []);

  const closeMenu = useCallback(() => {
    setMenu((prev) => ({ ...prev, open: false, items: null, triggerId: null }));
  }, []);

  // prevent native context menu app-wide, and close the custom menu when right-clicking outside triggers
  useEffect(() => {
    const onContext = (e: MouseEvent) => {
      // always prevent default native context menu
      e.preventDefault();

      // if target isn't within a rightclick trigger, close menu
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (!target.closest("[data-rightclick-trigger]")) {
        setMenu((prev) => ({ ...prev, open: false, items: null, triggerId: null }));
      }
    };

    // also close menu on left click anywhere
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (
        !target.closest("[data-rightclick-trigger]") &&
        !target.closest("[data-rightclick-menu]")
      ) {
        setMenu((prev) => ({ ...prev, open: false, items: null, triggerId: null }));
      }
    };

    document.addEventListener("contextmenu", onContext);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, []);

  // adjust menu position to remain in viewport after render
  useEffect(() => {
    if (!menu.open) return;
    const el = menuRef.current;
    if (!el) return;

    const { innerWidth: w, innerHeight: h } = window;
    const rect = el.getBoundingClientRect();

    let left = menu.x;
    let top = menu.y;

    // keep 1px gap from edges
    const gap = 1;

    if (left + rect.width + gap > w) {
      left = Math.max(gap, w - rect.width - gap);
    }
    if (top + rect.height + gap > h) {
      top = Math.max(gap, h - rect.height - gap);
    }
    if (left < gap) left = gap;
    if (top < gap) top = gap;

    if (left !== menu.x || top !== menu.y) {
      setMenu((prev) => ({ ...prev, x: left, y: top }));
    }
  }, [menu.open, menu.x, menu.y]);

  return (
    <RightClickContext.Provider value={{ menu, openMenu, updateMenu, closeMenu }}>
      {children}

      {/* render single global menu */}
      {menu.open && (
        <div
          data-rightclick-menu
          ref={menuRef}
          style={{ position: "fixed", left: menu.x, top: menu.y, zIndex: 20000 }}
        >
          {menu.items}
        </div>
      )}
    </RightClickContext.Provider>
  );
}
